/*
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, you can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * Copyright Oxide Computer Company
 */

const remoteAccess = 'https://docs.oxide.computer/guides/remote-access'

// URLs used in inline prose links where the label is chosen to fit the
// surrounding sentence. For links with a canonical label, use docLinks instead.
export const links = {
  cloudInit: 'https://docs.cloud-init.io/',
  cloudInitFormat: 'https://docs.cloud-init.io/en/latest/explanation/format.html',
  cloudInitExamples: 'https://docs.cloud-init.io/en/latest/reference/examples.html',
  firewallRulesDocs:
    'https://docs.oxide.computer/guides/configuring-guest-networking#_firewall_rules',
  globbingDocs: 'https://docs.oxide.computer/guides/alerts/overview#_globbing',
  preparingImagesDocs:
    'https://docs.oxide.computer/guides/creating-and-sharing-images#_preparing_images_for_import',
  identityProvidersDocs: 'https://docs.oxide.computer/guides/operator/identity-providers',
  // TODO: link to section
  instanceBootDiskDocs: 'https://docs.oxide.computer/guides/deploying-workloads',
  oxqlSchemaDocs: (metric: string) =>
    `https://docs.oxide.computer/guides/metrics/timeseries-schemas#_${metric.replace(':', '')}`,
  siloTlsCertsDocs:
    'https://docs.oxide.computer/guides/system/system-setup#tls-certificate',
  transitIpsDocs:
    'https://docs.oxide.computer/guides/configuring-guest-networking#_example_4_software_routing_tunnels',
  troubleshootingAccess:
    'https://docs.oxide.computer/guides/operator/faq#_how_do_i_fix_the_something_went_wrong_error',
  webhooksGuide: 'https://docs.oxide.computer/guides/alerts/webhooks',
  webhookSecretsDocs: 'https://docs.oxide.computer/guides/alerts/webhooks#_secrets',
  webhooksApiDocs: 'https://docs.oxide.computer/api/webhook_receiver_create',
}

// Links with a canonical label, used in DocsPopover and SideModalFormDocs.
export const docLinks = {
  access: {
    href: 'https://docs.oxide.computer/guides/configuring-access',
    linkText: 'Access Control',
  },
  affinity: {
    href: 'https://docs.oxide.computer/guides/deploying-workloads#_affinity_and_anti_affinity',
    linkText: 'Anti-Affinity Groups',
  },
  alerts: {
    href: 'https://docs.oxide.computer/guides/alerts/overview',
    linkText: 'Alerts Overview',
  },
  auditLog: {
    href: 'https://docs.oxide.computer/guides/operator/audit-log',
    linkText: 'Audit Log',
  },
  deviceTokens: {
    href: 'https://docs.oxide.computer/guides/user-settings#_access_tokens',
    linkText: 'Access Tokens',
  },
  externalSubnets: {
    href: 'https://docs.oxide.computer/guides/operator/ip-pool-management#_using_subnet_pools',
    linkText: 'Subnet Pools',
  },
  disks: {
    href: 'https://docs.oxide.computer/guides/managing-disks-and-snapshots',
    linkText: 'Disks and Snapshots',
  },
  firewallRules: {
    href: links.firewallRulesDocs,
    linkText: 'Firewall Rules',
  },
  fleetRoleMappings: {
    href: 'https://docs.oxide.computer/guides/configuring-access#_silo_admin_group_and_mapped_fleet_roles',
    linkText: 'Fleet Role Mappings',
  },
  floatingIps: {
    href: 'https://docs.oxide.computer/guides/managing-floating-ips',
    linkText: 'Floating IPs',
  },
  gateways: {
    href: 'https://docs.oxide.computer/guides/configuring-guest-networking#internet-gateway',
    linkText: 'Internet Gateways',
  },
  keyConceptsIam: {
    href: 'https://docs.oxide.computer/guides/key-entities-and-concepts#iam-policy',
    linkText: 'Key Concepts',
  },
  identityProviders: {
    href: links.identityProvidersDocs,
    linkText: 'Identity Providers',
  },
  images: {
    href: 'https://docs.oxide.computer/guides/creating-and-sharing-images',
    linkText: 'Images',
  },
  autoRestart: {
    href: 'https://docs.oxide.computer/guides/managing-instances#_update_instances',
    linkText: 'Instance Auto-Restart',
  },
  instanceActions: {
    href: 'https://docs.oxide.computer/guides/managing-instances',
    linkText: 'Instance Actions',
  },
  oxql: {
    href: 'https://docs.oxide.computer/guides/metrics/oxql-tutorial#_oxql_quick_start',
    linkText: 'OxQL',
  },
  oxqlSchemas: {
    href: 'https://docs.oxide.computer/guides/metrics/timeseries-schemas',
    linkText: 'Timeseries schemas',
  },
  keyConceptsProjects: {
    href: 'https://docs.oxide.computer/guides/key-entities-and-concepts#_projects',
    linkText: 'Key Concepts',
  },
  projects: {
    href: 'https://docs.oxide.computer/guides/onboarding-projects',
    linkText: 'Projects',
  },
  quickStart: {
    href: 'https://docs.oxide.computer/guides/quickstart',
    linkText: 'Quick Start',
  },
  remoteAccess: {
    href: remoteAccess,
    linkText: 'Remote Access',
  },
  resourceManagement: {
    href: 'https://docs.oxide.computer/guides/operator/resource-management',
    linkText: 'Resource Management',
  },
  scim: {
    href: 'https://docs.oxide.computer/guides/operator/identity-providers#scim',
    linkText: 'SCIM',
  },
  serialConsole: {
    href: remoteAccess + '#serial-console',
    linkText: 'Serial Console',
  },
  routers: {
    href: 'https://docs.oxide.computer/guides/configuring-guest-networking#_custom_routers',
    linkText: 'Custom Routers',
  },
  routes: {
    href: 'https://docs.oxide.computer/guides/configuring-guest-networking#vpc-subnet',
    linkText: 'VPC Subnet Routing',
  },
  sleds: {
    href: 'https://docs.oxide.computer/guides/architecture/rack-components#_server_sled',
    linkText: 'Server Sleds',
  },
  snapshots: {
    href: 'https://docs.oxide.computer/guides/managing-disks-and-snapshots#_snapshots',
    linkText: 'Disks and Snapshots',
  },
  ssh: {
    href: remoteAccess + '#ssh',
    linkText: 'SSH',
  },
  sshKeys: {
    href: 'https://docs.oxide.computer/guides/user-settings#manage-ssh-keys',
    linkText: 'SSH Keys',
  },
  storage: {
    href: 'https://docs.oxide.computer/guides/architecture/os-hypervisor-storage#_storage',
    linkText: 'Storage',
  },
  systemIpPools: {
    href: 'https://docs.oxide.computer/guides/operator/ip-pool-management',
    linkText: 'IP Pools',
  },
  subnetPools: {
    href: 'https://docs.oxide.computer/guides/operator/ip-pool-management#_using_subnet_pools',
    linkText: 'Subnet Pools',
  },
  systemMetrics: {
    href: 'https://docs.oxide.computer/guides/operator/resource-management#_calculating_utilization',
    linkText: 'Utilization',
  },
  systemSiloCreate: {
    href: 'https://docs.oxide.computer/guides/system/completing-rack-config#_create_a_silo',
    linkText: 'Create Silo',
  },
  systemSilo: {
    href: 'https://docs.oxide.computer/guides/operator/silo-management',
    linkText: 'Silos',
  },
  systemUpdate: {
    href: 'https://docs.oxide.computer/guides/operator/system-update',
    linkText: 'System Update',
  },
  instances: {
    href: 'https://docs.oxide.computer/guides/deploying-workloads',
    linkText: 'Instances',
  },
  networkInterfaces: {
    href: 'https://docs.oxide.computer/guides/deploying-workloads#_virtual_nics',
    linkText: 'Virtual NICs',
  },
  vpcs: {
    href: 'https://docs.oxide.computer/guides/configuring-guest-networking',
    linkText: 'Networking',
  },
  webhookReceivers: {
    href: links.webhooksGuide,
    linkText: 'Webhook Receivers',
  },
  webhookSecretRotation: {
    href: 'https://docs.oxide.computer/guides/alerts/reliable-receivers#_zero_downtime_webhook_secret_rotation',
    linkText: 'Secret Rotation',
  },
}
